import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Dialog } from '../ui/Dialog.js';
import { Button } from '../ui/Button.js';
import { Field, Input, Select } from '../ui/Field.js';
import {
  CUSTOM_NAME_MAX,
  findByNotes,
  findTuning,
  formatCustomTuning,
  noteFrequency,
  noteToMidi,
  INSTRUMENTS,
  type Instrument,
  type Tuning
} from '../../data/tunings.js';
import { addCustomTuning, listCustomTunings } from '../../data/customTunings.js';
import { useAuthStore } from '../../stores/authStore.js';
import { usePlanStore } from '../../stores/planStore.js';
import { toPersianDigits } from '../../utils/timeUtils.js';

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const OCTAVES = [0, 1, 2, 3, 4, 5, 6];

interface Row {
  note: string;
  octave: number;
}

const splitNote = (n: string): Row => ({ note: n.slice(0, -1), octave: Number(n.slice(-1)) });

export interface CustomTuningDialogProps {
  open: boolean;
  onClose: () => void;
  /** Guitar type being edited (string count). */
  instrument: Instrument;
  strings: number;
  /** Notes (low to high) to prefill; falls back to the first catalogue tuning of the type when the count differs. */
  initialNotes: string[];
  /** Called with the tuning to select: a catalogue entry when the notes already exist there, otherwise the saved custom one. */
  onCreated: (tuning: Tuning) => void;
}

/** FRE-139: build a custom tuning string by string (lowest string first), save it on this device and select it. */
export const CustomTuningDialog: React.FC<CustomTuningDialogProps> = ({ open, onClose, instrument, strings, initialNotes, onCreated }) => {
  const [rows, setRows] = useState<Row[]>(() => initialNotes.map(splitNote));
  const [name, setName] = useState('');

  useEffect(() => {
    if (open) {
      setRows(initialNotes.map(splitNote));
      setName('');
    }
    // Only re-prefill when the dialog opens; later prop changes must not wipe what the player is editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const notes = useMemo(() => rows.map((r) => `${r.note}${r.octave}`), [rows]);

  const warnings = useMemo(() => {
    const out: string[] = [];
    const midi = notes.map(noteToMidi);
    if (midi.some((m, i) => i > 0 && m < midi[i - 1])) out.push('نت‌ها از بم به زیر مرتب نیستند؛ اگر عمدی است (مثل کوک نشویل) ادامه دهید.');
    else if (midi.some((m, i) => i > 0 && m === midi[i - 1])) out.push('دو سیم کناریِ هم دقیقاً یک نت دارند؛ اگر عمدی است ادامه دهید.');
    if (notes.length > 0 && noteFrequency(notes[0]) < 45) {
      out.push('نت‌های خیلی بم روی میکروفون گوشی دقیق نیستند؛ برای نتیجه بهتر سیم را روی هارمونیک دوازدهم بزنید.');
    }
    return out;
  }, [notes]);

  const setRow = (i: number, patch: Partial<Row>) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const save = () => {
    const existing = findByNotes(notes);
    if (existing) {
      onCreated(existing);
      onClose();
      return;
    }

    // F-128: Check guest and free limits
    const { isGuest } = useAuthStore.getState();
    const isPlusOrAdmin = usePlanStore.getState().isPlusOrAdmin();
    const openUpgradeDialog = usePlanStore.getState().openUpgradeDialog;

    if (isGuest) {
      openUpgradeDialog({
        feature: 'CUSTOM_TUNINGS',
        isGuestPrompt: true,
        message: 'ساخت و ذخیره کوک‌های سفارشی مخصوص کاربران عضو است. لطفاً وارد شوید یا ثبت‌نام کنید.'
      });
      return;
    }

    const currentCustoms = listCustomTunings();
    if (!isPlusOrAdmin && currentCustoms.length >= 5) {
      openUpgradeDialog({
        feature: 'CUSTOM_TUNINGS',
        limit: 5,
        used: currentCustoms.length,
        message: 'در پلن رایگان حداکثر ۵ کوک سفارشی می‌توانید ذخیره کنید. برای ساخت کوک‌های نامحدود، به پلاس ارتقا دهید.'
      });
      return;
    }

    const stored = formatCustomTuning(name, notes);
    addCustomTuning(stored);
    const tuning = findTuning(stored);
    if (tuning) onCreated(tuning);
    onClose();
  };

  const lowHz = notes.length > 0 ? noteFrequency(notes[0]) : 0;

  const dialog = (
    <Dialog
      open={open}
      onClose={onClose}
      title="ساخت کوک جدید"
      titleHeadingLevel="h3"
      description={`${INSTRUMENTS[instrument].label} ${toPersianDigits(strings)} سیم · هر سیم را از بم‌ترین سیم تنظیم کنید.`}
      size="sm"
      data-testid="custom-tuning-dialog"
      footer={
        <>
          <Button variant="primary" onClick={save} data-testid="custom-tuning-save">
            ذخیره و انتخاب
          </Button>
          <Button variant="ghost" onClick={onClose} data-testid="custom-tuning-cancel">
            انصراف
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <div className="flex flex-col gap-2">
          {rows.map((row, i) => {
            const stringNumber = rows.length - i;
            return (
              <div key={stringNumber} className="flex items-center gap-2">
                <span className="w-16 shrink-0 text-xs text-base-content/70">سیم {toPersianDigits(stringNumber)}</span>
                <Select
                  dir="ltr"
                  sizeVariant="sm"
                  aria-label={`نت سیم ${stringNumber}`}
                  value={row.note}
                  onChange={(e) => setRow(i, { note: e.target.value })}
                  data-testid={`custom-tuning-note-${stringNumber}`}
                >
                  {NOTE_NAMES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Select>
                <Select
                  dir="ltr"
                  sizeVariant="sm"
                  aria-label={`اکتاو سیم ${stringNumber}`}
                  value={String(row.octave)}
                  onChange={(e) => setRow(i, { octave: Number(e.target.value) })}
                  data-testid={`custom-tuning-octave-${stringNumber}`}
                >
                  {(OCTAVES.includes(row.octave) ? OCTAVES : [...OCTAVES, row.octave]).map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </Select>
              </div>
            );
          })}
        </div>

        <p dir="ltr" className="font-mono text-xs text-base-content/80 text-start" data-testid="custom-tuning-preview">
          {notes.join(' ')} · {lowHz.toFixed(1)} Hz
        </p>

        {warnings.length > 0 && (
          <div data-testid="custom-tuning-warning" className="flex flex-col gap-1 text-[11px] leading-5 text-warning">
            {warnings.map((w) => (
              <p key={w} className="m-0">
                {w}
              </p>
            ))}
          </div>
        )}

        <Field label="نام کوک (اختیاری)">
          <Input
            dir="ltr"
            isLatin
            maxLength={CUSTOM_NAME_MAX}
            placeholder="Custom"
            value={name}
            onChange={(e) => setName(e.target.value.replace(/[()]/g, ''))}
            data-testid="custom-tuning-name"
          />
        </Field>
      </div>
    </Dialog>
  );

  // Portal to <body>: the picker usually sits inside another dialog whose animated surface would clip a fixed child.
  return typeof document === 'undefined' ? dialog : createPortal(dialog, document.body);
};
